import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Bell, Send, AlertCircle, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const CrearNotificacionModal = ({ open, onClose, onNotificacionCreada }) => {
  const [loading, setLoading] = useState(false);
  const [editores, setEditores] = useState([]);
  const [formData, setFormData] = useState({
    mensaje: '',
    destinatarios: 'all_editors',
    prioridad: 'normal',
    tipo: 'mensaje_admin'
  });

  useEffect(() => {
    if (open) {
      loadEditores();
    }
  }, [open]);

  const loadEditores = async () => {
    try {
      const res = await axios.get(`${API}/admin/editores`);
      setEditores(res.data);
    } catch (error) {
      console.error('Error loading editors:', error);
      toast.error('Error al cargar la lista de editores');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.mensaje.trim()) {
      toast.error('El mensaje no puede estar vacío');
      return;
    }

    setLoading(true);
    try {
      // Preparar destinatarios
      let destinatarios = [];
      if (formData.destinatarios === 'all_editors') {
        destinatarios = ['all_editors'];
      } else if (formData.destinatarios === 'all_admins') {
        destinatarios = ['all_admins'];
      } else {
        // Destinatario específico
        destinatarios = [formData.destinatarios];
      }

      await axios.post(`${API}/notificaciones`, {
        tipo: formData.tipo,
        mensaje: formData.mensaje,
        destinatarios: destinatarios,
        prioridad: formData.prioridad,
        metadata: {
          creado_manualmente: true
        }
      });

      toast.success('Notificación enviada correctamente');
      
      // Reset form
      setFormData({
        mensaje: '',
        destinatarios: 'all_editors',
        prioridad: 'normal',
        tipo: 'mensaje_admin'
      });
      
      if (onNotificacionCreada) {
        onNotificacionCreada();
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating notification:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar notificación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-600" />
            Crear Notificación
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Destinatarios */}
          <div className="space-y-2">
            <Label htmlFor="destinatarios">Enviar a *</Label>
            <select
              id="destinatarios"
              value={formData.destinatarios}
              onChange={(e) => setFormData({ ...formData, destinatarios: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="all_editors">📢 Todos los Editores</option>
              <option value="all_admins">👥 Todos los Administradores</option>
              <optgroup label="Editores específicos">
                {editores.map(editor => {
                  const nombreCompleto = [editor.nombre, editor.apellidos]
                    .filter(Boolean)
                    .join(' ')
                    .trim();
                  return (
                    <option key={editor.id} value={editor.id}>
                      👤 {nombreCompleto} ({editor.email})
                    </option>
                  );
                })}
              </optgroup>
            </select>
            <p className="text-xs text-gray-500">
              {formData.destinatarios === 'all_editors' && '📌 Todos los editores deberán leer la notificación'}
            </p>
          </div>

          {/* Prioridad */}
          <div className="space-y-2">
            <Label htmlFor="prioridad">Prioridad *</Label>
            <div className="grid grid-cols-4 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, prioridad: 'baja' })}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData.prioridad === 'baja'
                    ? 'border-gray-500 bg-gray-100'
                    : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                <div className="text-center">
                  <Info className="w-5 h-5 mx-auto mb-1 text-gray-500" />
                  <span className="text-xs font-medium">Baja</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, prioridad: 'normal' })}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData.prioridad === 'normal'
                    ? 'border-blue-500 bg-blue-100'
                    : 'border-gray-200 hover:border-blue-400'
                }`}
              >
                <div className="text-center">
                  <Bell className="w-5 h-5 mx-auto mb-1 text-blue-500" />
                  <span className="text-xs font-medium">Normal</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, prioridad: 'alta' })}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData.prioridad === 'alta'
                    ? 'border-orange-500 bg-orange-100'
                    : 'border-gray-200 hover:border-orange-400'
                }`}
              >
                <div className="text-center">
                  <AlertCircle className="w-5 h-5 mx-auto mb-1 text-orange-500" />
                  <span className="text-xs font-medium">Alta</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, prioridad: 'urgente' })}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData.prioridad === 'urgente'
                    ? 'border-red-500 bg-red-100'
                    : 'border-gray-200 hover:border-red-400'
                }`}
              >
                <div className="text-center">
                  <AlertCircle className="w-5 h-5 mx-auto mb-1 text-red-500" />
                  <span className="text-xs font-medium">Urgente</span>
                </div>
              </button>
            </div>
          </div>

          {/* Mensaje */}
          <div className="space-y-2">
            <Label htmlFor="mensaje">Mensaje *</Label>
            <textarea
              id="mensaje"
              value={formData.mensaje}
              onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })}
              placeholder="Escribe tu mensaje aquí..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
              required
              maxLength={500}
            />
            <p className="text-xs text-gray-500 text-right">
              {formData.mensaje.length}/500 caracteres
            </p>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex gap-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-800">
                <p className="font-semibold mb-1">📌 Importante:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Los editores recibirán una notificación con sonido</li>
                  <li>Cada editor debe marcarla como leída individualmente</li>
                  <li>Verán quién más la ha leído en tiempo real</li>
                </ul>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.mensaje.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Notificación
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CrearNotificacionModal;
